import { useEffect } from 'react';
import { create } from 'zustand';
import apiClient from '../api/client';
import { API } from '../api/endpoints';
import { useGlobalStore } from '../stores/globalStore';
import { useConnectionStore } from '../stores/connectionStore';
import { PREF_KEYS, getUserPref, setUserPref } from './useUserPreferences';

/**
 * A few things the website remembers for a person wherever they sign in, not per browser: whether
 * they have seen the welcome tour and the newest What's New note they have seen. Kept on the server
 * (GET / PUT api/user/Me/State, DietechSoftwareMain) and in this browser's preferences
 * (useUserPreferences), which still work before the server answers, offline, or when the server
 * cannot be reached.
 *
 *  - Loaded once per user and organization after sign-in (`loadServerUserState`).
 *  - Reading: the server's value when it has one, else the browser's (`readUserState`).
 *  - Writing: both (`writeUserState`); a failed server write is ignored, the browser copy stays.
 *  - Moving over: a value the browser has and the server does not is sent up once, on load.
 *
 * The welcome tour and the What's New pop-up wait for `ready` (loaded, or failed), so nobody sees a
 * flash of something they already dismissed on another computer.
 */

/** The keys kept on the server. The API only accepts these (UserStateController.AllowedKeys). */
export const SERVER_USER_STATE_KEYS: readonly string[] = [PREF_KEYS.WELCOME_TOUR, PREF_KEYS.WHATS_NEW_SEEN];

interface StateResponse {
  available?: boolean;
  values?: Record<string, unknown>;
}

interface ServerUserState {
  /** `${connectionId}:${userId}` the values belong to; null before the first load. */
  owner: string | null;
  status: 'idle' | 'loading' | 'ready';
  /** Whether the server answered with its values (false: it failed, or had no user). */
  available: boolean;
  /** The server's values (after moving the browser's up), by key. */
  values: Record<string, unknown>;
}

const INITIAL: ServerUserState = { owner: null, status: 'idle', available: false, values: {} };

export const useServerUserStateStore = create<ServerUserState>(() => ({ ...INITIAL }));

let inflight: Promise<void> | null = null;

function ownerOf(userId: number): string {
  const connectionId = useConnectionStore.getState().selectedConnection?.connectionId ?? 0;
  return `${connectionId}:${userId}`;
}

function isKnownKey(key: string): boolean {
  return SERVER_USER_STATE_KEYS.includes(key);
}

/** Sends one value to the server. Never throws: the browser copy is already saved. */
async function putServerValue(key: string, value: unknown): Promise<boolean> {
  try {
    const { data } = await apiClient.put<{ saved?: boolean }>(API.userMeStateKey(key), JSON.stringify(value), {
      headers: { 'Content-Type': 'application/json' },
      // A website newer than its API (no route yet) must not raise the version-mismatch alarm.
      _skipVersionMismatch: true,
    } as never);
    return data?.saved === true;
  } catch {
    return false;
  }
}

/**
 * Loads the server's values for this user (once per user and organization; a second call while
 * one is running waits for it). Resolves when `status` is 'ready', whatever happened.
 */
export function loadServerUserState(userId: number): Promise<void> {
  if (userId <= 0) return Promise.resolve();
  const owner = ownerOf(userId);
  const current = useServerUserStateStore.getState();
  if (current.owner === owner && current.status !== 'idle') return inflight ?? Promise.resolve();

  useServerUserStateStore.setState({ owner, status: 'loading', available: false, values: {} });
  const run = (async () => {
    let available = false;
    const values: Record<string, unknown> = {};
    try {
      const { data } = await apiClient.get<StateResponse>(API.userMeState, { _skipVersionMismatch: true } as never);
      if (data && typeof data === 'object' && !Array.isArray(data) && data.available === true) {
        available = true;
        const got = data.values && typeof data.values === 'object' ? data.values : {};
        for (const [key, value] of Object.entries(got)) {
          if (isKnownKey(key) && value !== null && value !== undefined) values[key] = value;
        }
      }
    } catch {
      // Not reachable: the browser's copy is used.
    }
    // Signed out or switched organization while this ran: its answer belongs to nobody now.
    if (useServerUserStateStore.getState().owner !== owner) return;

    if (available) {
      for (const key of SERVER_USER_STATE_KEYS) {
        if (key in values) {
          // The server wins; keep this browser's copy in step for offline use.
          setUserPref(userId, key, values[key]);
        } else {
          const local = getUserPref<unknown>(userId, key, null);
          if (local !== null && local !== undefined) {
            values[key] = local;
            void putServerValue(key, local);
          }
        }
      }
    }
    useServerUserStateStore.setState({ status: 'ready', available, values });
  })();
  inflight = run;
  void run.finally(() => {
    if (inflight === run) inflight = null;
  });
  return run;
}

/** The value for this user: the server's when loaded and it has one, else this browser's. */
export function readUserState<T>(userId: number, key: string, fallback: T): T {
  const s = useServerUserStateStore.getState();
  if (s.owner === ownerOf(userId) && s.status === 'ready' && key in s.values) return s.values[key] as T;
  return getUserPref<T>(userId, key, fallback);
}

/** Saves the value in this browser and, for a server key, on the server (in the background). */
export function writeUserState<T>(userId: number, key: string, value: T): void {
  if (userId <= 0) return;
  setUserPref<T>(userId, key, value);
  if (!isKnownKey(key)) return;
  const s = useServerUserStateStore.getState();
  if (s.owner === ownerOf(userId)) useServerUserStateStore.setState({ values: { ...s.values, [key]: value } });
  void putServerValue(key, value);
}

/** Back to nothing loaded (sign-out, and between tests). */
export function resetServerUserState(): void {
  inflight = null;
  useServerUserStateStore.setState({ ...INITIAL });
}

/**
 * Starts the load for the signed-in user and says whether it is done (loaded or failed). Read and
 * write with `readUserState` / `writeUserState`.
 */
export function useServerUserState(): { ready: boolean; userId: number } {
  const userId = useGlobalStore((s) => s.userId);
  const connectionId = useConnectionStore((s) => s.selectedConnection?.connectionId ?? 0);
  const owner = `${connectionId}:${userId}`;
  const ready = useServerUserStateStore((s) => s.owner === owner && s.status === 'ready');

  useEffect(() => {
    if (userId > 0) void loadServerUserState(userId);
  }, [userId, connectionId]);

  return { ready, userId };
}
